import type { PowerUserFeatures } from "services/powerUser";

type BooleanFlagKey =
  | "initech-customer-portal-web"
  | "allow-boosting-gig-manually"
  | "message-support-button-visable"
  /** Permanent ops kill switch, off by design. Don't clean up as stale. */
  | "maintenance-mode"
  | "temp-show-gig-autoselect-toggle"
  | "manager-admin-clock-in"
  | "extra-positions"
  | "world-cup-positions"
  | "agent-web-header-button-admin"
  /** The onboarding takeover and the Ask Qwick entry restyle; effective only where agent-web-header-button-admin is on. */
  | "agent-web-onboarding"
  | "match-v3-web"
  | "require-po-number"
  // WORK-8452: "Does this shift include tips?" everywhere on web (ask, edit, display). Off = hidden and never required.
  | "fe-shift-includes-tips"
  /** WORK-10588: the wage picker offers one typed wage or Recommended (no Market Avg), warns against Recommended, and can update the saved position. */
  | "wage-recommendation-refresh"
  | "duplicate-onboarding-check"
  | "task-upload-allowed"
  /** CORE-9518: the Task posting type in the classic post form. The API gates task creation on the same key. */
  | "qwick-tasks"
  | "minimum-time-cancellation-reason-overhaul"
  | "MANUAL_PAY_REPLACE_adhoc"
  /**
   * Lets a gigpro admin pick the CREATE and SEND invoicing automation levels. Those two are acted
   * on by the daily invoicing batch — CREATE opens a real invoice on Invoiced.com, SEND emails it
   * to the customer — so they stay unselectable until that pipeline is trusted. Defaults to
   * `false` on purpose: if LaunchDarkly is unreachable, or the flag is absent from it entirely,
   * the options must be hidden rather than shown.
   */
  | "invoicing-automation-create-send"
  /** CORE-9000: the admin-only "Invoiced.com customer ID" Edit/Save section in the billing dialog's Invoice branch. */
  | "fe-invoiced-customer-id"
  /** Shows the Jobs plan entry points (Settings card). The plan state itself comes from `location.jobs_subscription`. */
  | "jobs-subscription"
  /** WORK-9008: admin pro page "Mark W-2 terminated" / "Restore W-2 employment" actions. */
  | "w2-termination"
  /** CORE-7697: lets managers drop an arrival pin on a venue. Existing pins show regardless. */
  | "venue-arrival-pin"
  /** WORK-9001: the admin pro Sick Time tab. Evaluated for the signed-in admin, not the pro being viewed. */
  | "pro-sick-time"
  /** Gates the four Hire offer moments together: Job details section, Home card, job-is-live step, rating prompt. */
  | "hire-offer-moments"
  /** Gates offering a working-interview tryout on a shift the business already posted (WORK-9178/9179). */
  | "tryout-shifts"
  /** Hire wave 2 (per location): the job page's working-interviews panel (WORK-9498). The API 404s the panel while off. */
  | "hire-working-interview-shifts"
  | "show-cancelled-by-name"
  /** CHAT-PARITY posting page (CORE-8650): internal kill switch. On = the posting page in the chat's takeover; off = today's canvas. */
  | "agent-web-posting"
  /** R3: who may switch to the posting page outside the chat ("Posting page V2"); effective only with agent-web-posting on. */
  | "agent-web-posting-opt-in"
  /** Posting page, M2: several days on one shift. While off the Days row offers One day only and "+ Add a shift" is hidden. */
  | "agent-web-posting-schedule"
  /** Posting page, M3: repeating shifts. While off the Days row has no Repeats. */
  | "agent-web-posting-recurring"
  /** Posting page, M4: events. While off the Event switch is hidden. */
  | "agent-web-posting-events"
  /** Replaces the Quill Finance dashboard with the in-house Report Builder. */
  | "billing-report-builder-web"
  /** Shared with the API, which only honors business break claims where it's on. Must be client-side enabled in LD. */
  | "two-party-breaks"
  | "connect-new-pros-filter"
  /** Declined tab shows each pro's specific reason, and includes suspended, confirmed-elsewhere and missing-requirements pros. */
  | "declined-offer-reasons"
  /** Log out of all devices: the Settings control and the SuperAdmin panel tool. The API 404s both endpoints while off. */
  | "log-out-of-all-devices"
  /** The Jobs renav: Jobs in the main menu, Hire pages in the main layout, applicants in Connect, and interviews on the Schedule. Shown to whoever `useHasJobsAccess` allows. */
  | "jobs-in-main-menu"
  /** Preferred Workers posts with the open-to-Marketplace backup on go to Marketplace first, with the Match lead-time hold. */
  | "preferred-pros-first-look"
  /** Single-pro Private Direct Offers list the offered-but-unconfirmed pro in the pros list on the Calendar detail and List views. */
  | "pdo-show-pending-offered-pro"
  /** CORE-9401: decorative Halloween easter eggs. Seasonal — turn off after Halloween. */
  | "halloween-easter-eggs"
  /** Admin "Close shift" toggle in the shift options menu (admin shifts and schedule). */
  | "close-a-shift-toggle"
  /** The Location-Info Boosting card and the admin Command-Center shift boost panel. Dark until rolled out. */
  | "location-shift-boost-toggles";

export type OverhaulPart = "navigation" | "theme" | "home";

type ValueFlags = {
  /**
   * 0: Engage Off
   * 1: Engage Phase 1 - MVP
   * 2: Engage Phase 2 - Assign a Gig
   * 3: Engage Phase 3 - Events Phase 1
   * 4: Engage Phase 4 - Events Phase 2
   * 5: Engage Phase 5 - Events Phase 3
   * 6: Engage Phase 6 - Tipping
   * 7: Engage Phase 7 - Post to Marketplace
   */
  "engage-phased": 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /**
   * 0: Hire Off
   * 1: Hire Phase 1
   * 2: Hire Phase 2 - Screening questionnaire
   * 3: Hire Phase 3 - Interview availability: manager's recurring windows (Settings default + per-job
   *    override), shown on the Hiring Schedule, and used to recommend slots. One ordinal flag can't
   *    gate these independently, so they share phase 3.
   * 4: Hire Phase 4 - Invite to apply: Add applicant on the job pipeline
   */
  "jobs-phased": 0 | 1 | 2 | 3 | 4;
  "interview-secret": string;
  /**
   * 0: Disabled
   * 1: Matches Tab Only
   * 2: Home Page + Matches Tab
   */
  "matches-v-3-mobile": 0 | 1 | 2;
  /**
   * PROJECT-196 Live Activity kill switch.
   * 0: Off
   * 1: On — full feature surface (self check-in, manager request, history drawer)
   */
  "live-activity": 0 | 1;
  /** ["all"] enables the Power Tools menu; an array of feature keys enables those features directly */
  "power-tools": Array<"all" | keyof PowerUserFeatures>;
  /**
   * The marketplace overhaul's parts, each rolled out on its own:
   * - navigation: one left sidebar (a drawer on mobile) and the Quincy chat rail
   * - theme: the `overhaul` theme tokens in index.css
   * - home: the actionable, modular Home (`domains/home`)
   */
  "marketplace-overhaul": Array<OverhaulPart>;
  /** YouTube IDs of the Hire tutorial; a flag so marketing can swap the video without a deploy. */
  "hire-tutorial-video": { landscape: string; vertical: string };
};
type ValueFlagKey = keyof ValueFlags;

export type FeatureFlagKey = BooleanFlagKey | ValueFlagKey;
export type FeatureFlagValue = Record<BooleanFlagKey, boolean> & ValueFlags;

export const defaultFlags: FeatureFlagValue = {
  "agent-web-header-button-admin": false,
  "agent-web-onboarding": false,
  "initech-customer-portal-web": false,
  "allow-boosting-gig-manually": false,
  "message-support-button-visable": false,
  "maintenance-mode": false,
  "temp-show-gig-autoselect-toggle": false,
  "engage-phased": 0,
  "jobs-phased": 0,
  "manager-admin-clock-in": false,
  "extra-positions": false,
  "world-cup-positions": false,
  "interview-secret": "",
  "minimum-time-cancellation-reason-overhaul": false,
  "match-v3-web": false,
  "matches-v-3-mobile": 0,
  "power-tools": [],
  "marketplace-overhaul": [],
  "hire-tutorial-video": { landscape: "", vertical: "" },
  "require-po-number": false,
  "agent-web-posting": false,
  "agent-web-posting-opt-in": false,
  "agent-web-posting-schedule": false,
  "agent-web-posting-recurring": false,
  "agent-web-posting-events": false,
  "fe-shift-includes-tips": false,
  "wage-recommendation-refresh": false,
  "duplicate-onboarding-check": true,
  "live-activity": 0,
  "task-upload-allowed": false,
  "qwick-tasks": false,
  MANUAL_PAY_REPLACE_adhoc: false,
  "invoicing-automation-create-send": false,
  "fe-invoiced-customer-id": false,
  "jobs-subscription": false,
  "w2-termination": false,
  "venue-arrival-pin": false,
  "pro-sick-time": false,
  "hire-offer-moments": false,
  "tryout-shifts": false,
  "hire-working-interview-shifts": false,
  "show-cancelled-by-name": false,
  "billing-report-builder-web": false,
  "two-party-breaks": false,
  "connect-new-pros-filter": false,
  "declined-offer-reasons": false,
  "log-out-of-all-devices": false,
  "jobs-in-main-menu": false,
  "preferred-pros-first-look": false,
  "pdo-show-pending-offered-pro": false,
  "halloween-easter-eggs": false,
  "close-a-shift-toggle": false,
  "location-shift-boost-toggles": false,
};
